import React, { Component } from 'react';
import { Switch, Route, Link, withRouter, Redirect } from 'react-router-dom';
import moment from 'moment';
import { saveAs } from 'file-saver';
import { pdf } from '@react-pdf/renderer';
import { UncontrolledDropdown, DropdownToggle, DropdownMenu, DropdownItem } from 'reactstrap';
import API from '../../utils/API';
import VenueSearchModal from '../../components/sourced-venues/venueSearchModal';
import VenueDetail from './venueDetail';
import LoadingWizard from '../../components/spinner/wizard';
import { PIPELINE } from './components/VenuePipelineStrip';
import VenueListPDF from './components/VenueListPDF';

const fmt = (d) => (d ? moment(d).format('MMM D') : '—');
const money = (n) => (n && n > 0) ? `$${Number(n).toLocaleString()}` : '—';

const normalizeStatus = (status) => {
	if (!status) return 'Draft';
	if (typeof status === 'string') return status;
	return status.value || 'Draft';
};

const normalizeProposalVenue = (proposalVenue) => ({
	...proposalVenue,
	status: normalizeStatus(proposalVenue?.status),
	brandName: proposalVenue?.brandName || null
});

const getStatusDef = (status) => PIPELINE.find(p => p.key === status) || PIPELINE[0];

/* ── CSV helpers ─────────────────────────────────────────────── */
const csvEsc = (val) => {
	const s = val === null || val === undefined ? '' : String(val);
	if (s.includes(',') || s.includes('"') || s.includes('\n')) {
		return `"${s.replace(/"/g, '""')}"`;
	}
	return s;
};

const fmtCsv = (d) => (d ? moment(d).format('MMM D, YYYY') : '');
const moneyCsv = (n) => (n && n > 0) ? Number(n).toFixed(2) : '';

const buildCSV = (venues) => {
	const headers = [
		'Venue Name', 'Brand', 'Location', 'Status',
		'Date Sent', 'Response Date', 'Quoted Room Rate',
		'Meeting Space Cost', 'F&B Minimum', 'Contract Value',
		'Proposal Expiration', 'Rooms on Hold'
	];
	const rows = venues.map((pv) => {
		const venue = pv.venue || {};
		return [
			venue.name || '',
			pv.brandName || '',
			venue.location || '',
			normalizeStatus(pv.status),
			fmtCsv(pv.sentDate),
			fmtCsv(pv.responseDate),
			moneyCsv(pv.quotedRoomRate),
			moneyCsv(pv.quotedMeetingSpaceCost),
			moneyCsv(pv.quotedFBMinimum),
			moneyCsv(pv.contractValue),
			fmtCsv(pv.proposalExpirationDate),
			pv.roomsOnHold || ''
		].map(csvEsc).join(',');
	});
	return [headers.map(csvEsc).join(','), ...rows].join('\n');
};

/* ── Filter summary for PDF ──────────────────────────────────── */
const buildFilterSummary = ({ activeStatus, activeBrand }) => {
	const parts = [];
	if (activeStatus) parts.push(`Status: ${PIPELINE.find(p => p.key === activeStatus)?.label || activeStatus}`);
	if (activeBrand)  parts.push(`Brand: ${activeBrand}`);
	return parts.join(' · ');
};

/**
 * Venues — primary work surface for a proposal request.
 * Filters: Status · Brand
 * Header CTA: [ ↓ Download ▾ ]  …  [ + Add Venues ]
 */
class Venues extends Component {
	constructor(props) {
		super(props);
		this.state = {
			proposalVenues: [],
			loading: true,
			searchModalOpen: false,
			downloadingPdf: false,
			/* filters */
			activeStatus: null,
			activeBrand:  null
		};
	}

	async componentDidMount() {
		await this.loadVenues();
		this.syncFiltersFromUrl();
	}

	componentDidUpdate(prevProps) {
		if (prevProps.location?.search !== this.props.location?.search) {
			this.syncFiltersFromUrl();
		}
	}

	syncFiltersFromUrl = () => {
		const params = new URLSearchParams(this.props.location?.search);
		const statusParam = params.get('status');
		const brandParam  = params.get('brand');
		const status = PIPELINE.some(p => p.key === statusParam) ? statusParam : null;
		const activeBrand = brandParam || null;
		this.setState((prev) => ({
			activeStatus: status      !== prev.activeStatus ? status      : prev.activeStatus,
			activeBrand:  activeBrand !== prev.activeBrand  ? activeBrand : prev.activeBrand
		}));
	};

	loadVenues = async () => {
		const { orgId, requestId } = this.props;
		this.setState({ loading: true });
		try {
			const res = await API().get(`Organizations/${orgId}/proposal-requests/${requestId}/venues`);
			const proposalVenues = (res.data || []).map(normalizeProposalVenue);
			this.setState({ proposalVenues, loading: false });
		} catch (e) {
			console.error('Error loading venues:', e);
			this.setState({ loading: false });
		}
	};

	toggleSearchModal = () => this.setState({ searchModalOpen: !this.state.searchModalOpen });

	handleVenuesAdded = async () => {
		await this.loadVenues();
		this.props.refreshSectionCounts && this.props.refreshSectionCounts();
		this.setState({ searchModalOpen: false });
	};

	updateVenue = (venueId, updatedData) => {
		this.setState({
			proposalVenues: this.state.proposalVenues.map(v =>
				v._id === venueId ? normalizeProposalVenue({ ...v, ...updatedData }) : v
			)
		});
	};

	removeVenue = async (proposalVenueId) => {
		const { orgId, history, requestId } = this.props;
		try {
			await API().delete(`Organizations/${orgId}/proposal-venues/${proposalVenueId}`);
			this.setState({ proposalVenues: this.state.proposalVenues.filter(v => v._id !== proposalVenueId) });
			this.props.refreshSectionCounts && this.props.refreshSectionCounts();
			history.push(`/${orgId}/proposal-requests/${requestId}/venues`);
		} catch (e) {
			console.error('Error removing venue:', e);
		}
	};

	pushUrlFilters = (nextStatus, nextBrand) => {
		const { history, orgId, requestId } = this.props;
		const params = new URLSearchParams();
		if (nextStatus) params.set('status', nextStatus);
		if (nextBrand)  params.set('brand',  nextBrand);
		const query = params.toString();
		history.push(`/${orgId}/proposal-requests/${requestId}/venues${query ? `?${query}` : ''}`);
	};

	onStatusChange = (e) => this.pushUrlFilters(e.target.value || null, this.state.activeBrand);
	onBrandChange  = (e) => this.pushUrlFilters(this.state.activeStatus, e.target.value || null);
	clearFilters   = () => this.pushUrlFilters(null, null);

	getVisible = () => {
		const { proposalVenues, activeStatus, activeBrand } = this.state;
		return proposalVenues.filter((pv) => {
			if (activeStatus && pv.status !== activeStatus) return false;
			if (activeBrand  && pv.brandName !== activeBrand) return false;
			return true;
		});
	};

	/* ── export ───────────────────────────────────────────────── */
	downloadCSV = () => {
		const visible = this.getVisible();
		const csv = buildCSV(visible);
		const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
		const requestName = this.props.request?.eventDetails?.eventName || 'venues';
		const safe = String(requestName).replace(/[^a-z0-9]+/gi, '-').replace(/^-+|-+$/g, '').toLowerCase() || 'venues';
		saveAs(blob, `${safe}-venues-${moment().format('YYYY-MM-DD')}.csv`);
	};

	downloadPDF = async () => {
		if (this.state.downloadingPdf) return;
		this.setState({ downloadingPdf: true });
		try {
			const visible = this.getVisible();
			const { activeStatus, activeBrand } = this.state;
			const filterSummary = buildFilterSummary({ activeStatus, activeBrand });
			const requestName = this.props.request?.eventDetails?.eventName || 'Proposal Request';
			const document = (
				<VenueListPDF
					venues={visible}
					requestName={requestName}
					filterSummary={filterSummary}
				/>
			);
			const blob = await pdf(document).toBlob();
			const safe = String(requestName).replace(/[^a-z0-9]+/gi, '-').replace(/^-+|-+$/g, '').toLowerCase() || 'venues';
			saveAs(blob, `${safe}-venues-${moment().format('YYYY-MM-DD')}.pdf`);
		} catch (err) {
			console.error('Error generating venues PDF:', err);
			window.alert('Could not generate the PDF right now. Please try again.');
		} finally {
			this.setState({ downloadingPdf: false });
		}
	};

	render() {
		const { orgId, requestId } = this.props;
		const { proposalVenues, loading, searchModalOpen, downloadingPdf, activeStatus, activeBrand } = this.state;

		const totalVenues = proposalVenues.length;
		const visible     = this.getVisible();
		const hasFilter   = !!(activeStatus || activeBrand);

		/* Brand options derived from loaded data */
		const brandCounts = proposalVenues.reduce((acc, pv) => {
			if (!pv.brandName) return acc;
			acc[pv.brandName] = (acc[pv.brandName] || 0) + 1;
			return acc;
		}, {});
		const availableBrands = Object.keys(brandCounts).sort((a, b) => a.localeCompare(b));

		return (
			<>
				<div className="pr-content-wrap">
					<div className="pr-content">
						<div className="pr-content-inner" style={{ maxWidth: 'none' }}>

							{/* ── Header ─────────────────────────────────────── */}
							<div className="pr-venues-header">
								<div className="pr-venues-title-block">
									<h1 className="pr-section-title">Venues</h1>
									<p className="pr-section-sub">
										{totalVenues === 0
											? 'Add venues that match this request, then track each proposal as it comes in.'
											: `Tracking ${totalVenues} ${totalVenues === 1 ? 'venue' : 'venues'}.`
										}
									</p>
								</div>

								<div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
									{/* Download dropdown */}
									{totalVenues > 0 && (
										<UncontrolledDropdown>
											<DropdownToggle
												className="pr-btn-ghost"
												style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
											>
												<i className="las la-download" />
												Download
												<i className="las la-angle-down" style={{ fontSize: 11 }} />
											</DropdownToggle>
											<DropdownMenu className="columnDropdown p-20">
												<h5 className="mb-10">Export {visible.length} {visible.length === 1 ? 'venue' : 'venues'}</h5>
												<DropdownItem onClick={this.downloadCSV}>
													<i className="las la-file-csv mr-10" />
													Download CSV
												</DropdownItem>
												<DropdownItem onClick={this.downloadPDF} disabled={downloadingPdf}>
													<i className="las la-file-pdf mr-10" />
													{downloadingPdf ? 'Preparing PDF…' : 'Download PDF'}
												</DropdownItem>
											</DropdownMenu>
										</UncontrolledDropdown>
									)}

									{/* Add Venues */}
									<button
										type="button"
										className="pr-btn-primary"
										onClick={this.toggleSearchModal}
									>
										<i className="las la-plus" />
										Add Venues
									</button>
								</div>
							</div>

							{/* ── Empty state ─────────────────────────────────── */}
							{!loading && totalVenues === 0 && (
								<div className="pr-empty">
									<i className="las la-hotel" />
									<h3>No venues added yet</h3>
									<p>Search and add venues that match your event's location, size, and dates.</p>
									<button
										type="button"
										className="pr-btn-primary"
										onClick={this.toggleSearchModal}
									>
										<i className="las la-search-location" />
										Find Venues
									</button>
								</div>
							)}

							{/* ── Filter bar ──────────────────────────────────── */}
							{totalVenues > 0 && (
								<div className="pr-filter-bar">
									{/* Status */}
									<div className="pr-filter-select-wrap">
										<select
											className="pr-filter-select"
											value={activeStatus || ''}
											onChange={this.onStatusChange}
										>
											<option value="">All Statuses</option>
											{PIPELINE.map(p => (
												<option key={p.key} value={p.key}>{p.label}</option>
											))}
										</select>
										<i className="las la-angle-down pr-filter-select-arrow" />
									</div>

									{/* Brand */}
									<div className="pr-filter-select-wrap">
										<select
											className="pr-filter-select"
											value={activeBrand || ''}
											onChange={this.onBrandChange}
											disabled={availableBrands.length === 0}
										>
											<option value="">All Brands</option>
											{availableBrands.map(b => (
												<option key={b} value={b}>{b} ({brandCounts[b]})</option>
											))}
										</select>
										<i className="las la-angle-down pr-filter-select-arrow" />
									</div>

									{/* Clear */}
									{hasFilter && (
										<button
											type="button"
											className="pr-btn-ghost"
											onClick={this.clearFilters}
											style={{ whiteSpace: 'nowrap', fontSize: 12, padding: '5px 12px' }}
										>
											<i className="las la-times" style={{ marginRight: 4 }} />
											Clear
										</button>
									)}
								</div>
							)}

							{/* ── Venue list ──────────────────────────────────── */}
							{!loading && visible.length > 0 && (
								<div className="pr-venue-list">
									{visible.map(pv => {
										const venue = pv.venue || {};
										const def   = getStatusDef(pv.status || 'Draft');
										const active = (this.props.match?.params?.venueId) === pv._id;

										return (
											<Link
												key={pv._id}
												to={`/${orgId}/proposal-requests/${requestId}/venues/${pv._id}`}
												className={`pr-venue-row ${active ? 'active' : ''}`}
											>
												<div
													className="pr-venue-thumb"
													style={venue.primaryImage ? { backgroundImage: `url(${venue.primaryImage})` } : {}}
												>
													{!venue.primaryImage && <i className="las la-hotel" />}
												</div>

												<div className="pr-venue-info">
													<h3 className="pr-venue-name">{venue.name || 'Unnamed Venue'}</h3>
													<div className="pr-venue-meta">
														{venue.location && (
															<span>
																<i className="las la-map-marker-alt" /> {venue.location}
															</span>
														)}
														{pv.brandName && (
															<span className="pr-venue-brand-tag">
																{pv.brandName}
															</span>
														)}
														{pv.sentDate && <span>Sent {fmt(pv.sentDate)}</span>}
														{pv.responseDate && <span>Replied {fmt(pv.responseDate)}</span>}
													</div>
												</div>

												<div className="pr-venue-nums">
													<div className="pr-venue-num">
														<span className="label">Room rate</span>
														<span className="value">{money(pv.quotedRoomRate)}</span>
													</div>
													<div className="pr-venue-num">
														<span className="label">Expiration</span>
														<span className="value">{fmt(pv.proposalExpirationDate)}</span>
													</div>
												</div>

												<span
													className="pr-venue-status"
													style={{ background: `${def.color}1a`, color: def.color }}
												>
													<span
														style={{
															width: 6,
															height: 6,
															borderRadius: '50%',
															background: def.color,
															display: 'inline-block'
														}}
													/>
													{def.label}
												</span>
											</Link>
										);
									})}
								</div>
							)}

							{/* ── Nothing matches filter ──────────────────────── */}
							{!loading && totalVenues > 0 && visible.length === 0 && (
								<div className="pr-empty" style={{ padding: '40px 20px' }}>
									<p>No venues match the current filters.</p>
									<button
										type="button"
										className="pr-btn-ghost"
										onClick={this.clearFilters}
									>
										Clear filters
									</button>
								</div>
							)}
						</div>
					</div>
				</div>

				<LoadingWizard loading={loading} text="Loading venues" />

				{searchModalOpen && (
					<VenueSearchModal
						isOpen={searchModalOpen}
						toggle={this.toggleSearchModal}
						orgId={orgId}
						proposalRequestId={requestId}
						onVenuesAdded={this.handleVenuesAdded}
						mode="proposalRequest"
					/>
				)}

				{/* Slide-over detail */}
				<Switch>
					<Route
						path="/:organization/proposal-requests/:request/venues/:venueId"
						render={(routeProps) => {
							const venueId = routeProps.match.params.venueId;
							const proposalVenue = proposalVenues.find(v => v._id === venueId);
							if (loading) return null;
							if (!proposalVenue) {
								return <Redirect to={`/${orgId}/proposal-requests/${requestId}/venues`} />;
							}
							return (
								<>
									<Link
										to={`/${orgId}/proposal-requests/${requestId}/venues`}
										className="pr-overlay-backdrop"
										aria-label="Close venue details"
									/>
									<div className="pr-overlay">
										<VenueDetail
											proposalVenue={proposalVenue}
											orgId={orgId}
											requestId={requestId}
											updateVenue={this.updateVenue}
											removeVenue={this.removeVenue}
											onClose={() => this.props.history.push(
												`/${orgId}/proposal-requests/${requestId}/venues`
											)}
										/>
									</div>
								</>
							);
						}}
					/>
				</Switch>
			</>
		);
	}
}

export default withRouter(Venues);
